import React from 'react';
import { List, ListItem, ListItemButton, ListItemText, TextField, Stack, Typography, Button, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import roleService, { RoleDto } from '../../services/roleService';

interface RolesListProps {
  selectedRoleId: string | null;
  onSelect: (roleId: string | null) => void;
}

const RolesList: React.FC<RolesListProps> = ({ selectedRoleId, onSelect }) => {
  const [query, setQuery] = React.useState('');
  const [roles, setRoles] = React.useState<RoleDto[]>([]);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuRole, setMenuRole] = React.useState<RoleDto | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [blockedOpen, setBlockedOpen] = React.useState(false);
  const [blockedUsers, setBlockedUsers] = React.useState<Array<{ user_id:string; email:string; display_name?:string; first_name?:string; last_name?:string }>>([]);
  const load = React.useCallback(async () => {
    const list = await roleService.listRoles();
    setRoles(list);
  }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const handler = () => load();
    window.addEventListener('roles:refresh' as any, handler);
    return () => window.removeEventListener('roles:refresh' as any, handler);
  }, [load]);
  const filtered = React.useMemo(() => roles.filter(r => r.name.toLowerCase().includes(query.toLowerCase())), [roles, query]);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, role: RoleDto) => { setMenuAnchor(e.currentTarget); setMenuRole(role); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRole(null); };

  const askDelete = () => { setConfirmOpen(true); closeMenu(); };

  const doDelete = async () => {
    if (!menuRole) return;
    const id = menuRole.id;
    setConfirmOpen(false);
    try {
      await roleService.deleteRole(id);
      setRoles(prev => prev.filter(r => r.id !== id));
      if (String(id) === selectedRoleId) onSelect(null);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        const data = e.response?.data?.data || [];
        setBlockedUsers(data);
        setBlockedOpen(true);
      } else {
        console.error('Nie udało się usunąć roli:', e);
      }
    }
  };

  const fullName = (u: any) => (u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField size="small" fullWidth placeholder="Szukaj roli" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Button size="small" variant="contained" onClick={async () => { 
          try {
            const r = await roleService.createRole({ name: 'nowa rola', modules: {} }); 
            setRoles(prev => [...prev, r]);
            onSelect(String(r.id));
          } catch (e: any) {
            const status = e?.response?.status;
            if (status === 501) {
              alert('Dodawanie nowej roli jest niedostępne: tabela roles nie istnieje w bazie. Możesz na razie korzystać z ról zdefiniowanych w users.role.');
            } else if (status === 409) {
              alert('Rola o takiej nazwie już istnieje. Zmień nazwę i spróbuj ponownie.');
            } else {
              alert('Nie udało się utworzyć roli. Sprawdź połączenie z API.');
            }
          }
        }}>Nowa rola</Button>
      </Stack>
      <List dense disablePadding>
        {filtered.map(role => (
          <ListItem key={role.id} disablePadding secondaryAction={
            <IconButton edge="end" size="small" onClick={(e) => openMenu(e, role)} aria-label={`akcje roli ${role.name}`}>
              <MoreVert fontSize="small" />
            </IconButton>
          }>
            <ListItemButton selected={String(role.id) === selectedRoleId} onClick={() => onSelect(String(role.id))} sx={{ borderRadius: 1, mb: 0.5 }}>
              <ListItemText
                primary={<Stack direction="row" spacing={1} alignItems="center"><Typography>{role.name}</Typography></Stack>}
                secondary={<Typography variant="caption" color="text.secondary">{role.description || ''}</Typography>}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Menu akcji roli */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} anchorOrigin={{ vertical:'bottom', horizontal:'right' }} transformOrigin={{ vertical:'top', horizontal:'right' }}>
        <MenuItem onClick={() => { if (menuRole) onSelect(String(menuRole.id)); closeMenu(); }}>Edytuj</MenuItem>
        <MenuItem onClick={askDelete} sx={{ color: 'error.main' }}>Usuń</MenuItem>
      </Menu>

      {/* Potwierdzenie usunięcia */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Usunąć rolę?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć rolę "{menuRole?.name}"? Tej operacji nie można cofnąć.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Anuluj</Button>
          <Button onClick={doDelete} color="error" variant="contained">Usuń</Button>
        </DialogActions>
      </Dialog>

      {/* Blokada usunięcia – rola przypisana do użytkowników */}
      <Dialog open={blockedOpen} onClose={() => setBlockedOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nie można usunąć roli</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ta rola jest przypisana do następujących użytkowników. Zmień im rolę na inną, a następnie spróbuj ponownie.
          </DialogContentText>
          <ul style={{ marginTop: 12 }}>
            {blockedUsers.map(u => (<li key={u.user_id}>{fullName(u)} — {u.email}</li>))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockedOpen(false)} autoFocus>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RolesList;


